import React, { useState, useRef, useEffect, useCallback } from 'react';

const ImagePositionEditor = ({ imageUrl, onSave, currentSettings, previewDimensions, availableAspectRatios }) => {
  const [scale, setScale] = useState(currentSettings?.scale || 1);
  const [position, setPosition] = useState(currentSettings?.position || { x: 0, y: 0 });
  const [aspectRatio, setAspectRatio] = useState(currentSettings?.aspectRatio || '4:3');
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeHandle, setResizeHandle] = useState(null);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [initialScale, setInitialScale] = useState(1);
  const containerRef = useRef(null);

  const allAspectRatios = {
    '1:1': { ratio: 1, label: 'Carré (1:1)' },
    '4:3': { ratio: 4 / 3, label: 'Standard (4:3)' },
    '16:9': { ratio: 16 / 9, label: 'Large (16:9)' },
    '4:5': { ratio: 4 / 5, label: 'Portrait (4:5)' },
    '3:2': { ratio: 3 / 2, label: 'Photo (3:2)' },
    '21:9': { ratio: 21 / 9, label: 'Bannière Hero (ratio 21:9)' }
  };

  // Filter aspect ratios based on availableAspectRatios prop
  const aspectRatios = availableAspectRatios 
    ? Object.keys(allAspectRatios)
        .filter(key => availableAspectRatios.includes(key))
        .reduce((obj, key) => {
          obj[key] = allAspectRatios[key];
          return obj;
        }, {})
    : allAspectRatios;

  const handleMouseDown = (e) => {
    if (e.target.classList.contains('resize-handle')) return; // Don't drag when resizing
    e.preventDefault();
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  };

  const handleResizeStart = (e, handle) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    setResizeHandle(handle);
    setInitialScale(scale);
    setDragStart({
      x: e.clientX,
      y: e.clientY
    });
  };

  const handleResizeMove = useCallback((e) => {
    if (!isResizing || !containerRef.current) return;
    e.preventDefault();

    const deltaX = e.clientX - dragStart.x;
    const deltaY = e.clientY - dragStart.y;
    
    // Calculate scale change based on movement
    const scaleFactor = 0.005; // Sensitivity
    let scaleChange = 0;

    switch (resizeHandle) {
      case 'top-left':
      case 'bottom-left':
        scaleChange = -deltaX * scaleFactor;
        break;
      case 'top-right':
      case 'bottom-right':
        scaleChange = deltaX * scaleFactor;
        break;
      case 'top':
        scaleChange = -deltaY * scaleFactor;
        break;
      case 'bottom':
        scaleChange = deltaY * scaleFactor;
        break;
      case 'left':
        scaleChange = -deltaX * scaleFactor;
        break;
      case 'right':
        scaleChange = deltaX * scaleFactor;
        break;
      default:
        break;
    }

    const newScale = Math.max(0.5, Math.min(3, initialScale + scaleChange));
    setScale(newScale);
  }, [isResizing, dragStart, resizeHandle, initialScale]);

  const handleMouseMove = useCallback((e) => {
    if (isResizing) {
      handleResizeMove(e);
      return;
    }
    if (!isDragging || !containerRef.current) return;

    e.preventDefault();
    
    const newX = e.clientX - dragStart.x;
    const newY = e.clientY - dragStart.y;

    setPosition({
      x: newX,
      y: newY
    });
  }, [isDragging, isResizing, dragStart, handleResizeMove]);

  const handleMouseUp = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    setIsResizing(false);
    setResizeHandle(null);
  }, []);

  // Touch support for mobile
  const handleTouchStart = useCallback((e) => {
    const touch = e.touches[0];
    setIsDragging(true);
    setDragStart({
      x: touch.clientX - position.x,
      y: touch.clientY - position.y
    });
  }, [position]);

  const handleTouchMove = useCallback((e) => {
    if (!isDragging || !containerRef.current) return;
    
    e.preventDefault();
    const touch = e.touches[0];
    
    const newX = touch.clientX - dragStart.x;
    const newY = touch.clientY - dragStart.y;

    setPosition({
      x: newX,
      y: newY
    });
  }, [isDragging, dragStart]);

  const handleTouchEnd = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging || isResizing) {
      window.addEventListener('mousemove', handleMouseMove, { passive: false });
      window.addEventListener('mouseup', handleMouseUp, { passive: false });
      window.addEventListener('touchmove', handleTouchMove, { passive: false });
      window.addEventListener('touchend', handleTouchEnd, { passive: false });
      
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
        window.removeEventListener('touchmove', handleTouchMove);
        window.removeEventListener('touchend', handleTouchEnd);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragging, isResizing]);

  const handleSave = () => {
    onSave({
      scale,
      position,
      aspectRatio,
      objectFit: 'cover' // Always use cover for custom positioning
    });
  };

  const handleReset = () => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  };

  const handleCenterHorizontally = () => {
    setPosition(prev => ({ ...prev, x: 0 }));
  };

  const handleCenterVertically = () => {
    setPosition(prev => ({ ...prev, y: 0 }));
  };

  const handleCenterBoth = () => {
    setPosition({ x: 0, y: 0 });
  };

  const handleAspectRatioChange = (newRatio) => {
    setAspectRatio(newRatio);
    // Reset position when aspect ratio changes to avoid white space
    setPosition({ x: 0, y: 0 });
  };

  const handleScaleChange = (newScale) => {
    setScale(newScale);
  };

  const getCursorForHandle = (handle) => {
    const cursors = {
      'top-left': 'nwse-resize',
      'top': 'ns-resize',
      'top-right': 'nesw-resize',
      'right': 'ew-resize',
      'bottom-right': 'nwse-resize',
      'bottom': 'ns-resize',
      'bottom-left': 'nesw-resize',
      'left': 'ew-resize'
    };
    return cursors[handle] || 'pointer';
  };

  return (
    <div style={{
      padding: '1.5rem',
      background: '#f8f9fa',
      borderRadius: '0.75rem',
      border: '2px solid #e0e0e0'
    }}>
      <h4 style={{ 
        margin: '0 0 1rem 0', 
        color: '#333',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem'
      }}>
        <i className="fas fa-sliders-h"></i>
        Éditeur d'image avancé
      </h4>

      {/* Aspect Ratio Selector */}
      <div style={{ marginBottom: '1rem' }}>
        <label style={{ 
          display: 'block', 
          marginBottom: '0.5rem', 
          fontWeight: 'bold',
          fontSize: '0.9rem'
        }}>
          📐 Format de l'image
        </label>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(3, 1fr)', 
          gap: '0.5rem' 
        }}>
          {Object.entries(aspectRatios).map(([key, { label }]) => (
            <button
              key={key}
              onClick={() => handleAspectRatioChange(key)}
              style={{
                padding: '0.6rem',
                border: aspectRatio === key ? '2px solid #007bff' : '1px solid #ddd',
                borderRadius: '0.5rem',
                background: aspectRatio === key ? '#e7f3ff' : 'white',
                cursor: 'pointer',
                fontSize: '0.85rem',
                fontWeight: aspectRatio === key ? 'bold' : 'normal',
                color: aspectRatio === key ? '#007bff' : '#666',
                transition: 'all 0.2s'
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Preview Area */}
      <div style={{ marginBottom: '1rem' }}>
        <label style={{ 
          display: 'block',
          fontWeight: 'bold',
          fontSize: '0.9rem',
          marginBottom: '0.5rem'
        }}>
          👁️ Aperçu (glissez l'image pour repositionner)
        </label>
        <div
          ref={containerRef}
          style={{
            width: previewDimensions?.width || '100%',
            height: previewDimensions?.height || 'auto',
            paddingBottom: previewDimensions?.height ? undefined : `${(1 / aspectRatios[aspectRatio].ratio) * 100}%`,
            position: 'relative',
            border: '2px dashed #007bff',
            borderRadius: '0.75rem',
            overflow: 'hidden',
            background: '#f8f9fa',
            cursor: isDragging ? 'grabbing' : isResizing ? 'nwse-resize' : 'grab',
            touchAction: 'none',
            WebkitUserSelect: 'none',
            MozUserSelect: 'none',
            msUserSelect: 'none',
            userSelect: 'none'
          }}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
        >
          <img
            src={imageUrl}
            alt="Preview"
            draggable={false}
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: `translate(calc(-50% + ${position.x}px), calc(-50% + ${position.y}px)) scale(${scale})`,
              maxWidth: 'none',
              width: 'auto',
              height: 'auto',
              minWidth: '100%',
              minHeight: '100%',
              objectFit: 'cover',
              transition: isDragging || isResizing ? 'none' : 'transform 0.05s ease-out',
              userSelect: 'none',
              pointerEvents: 'none',
              WebkitUserDrag: 'none'
            }}
          />
          
          {/* Resize Handles */}
          {['top-left', 'top', 'top-right', 'right', 'bottom-right', 'bottom', 'bottom-left', 'left'].map((handle) => (
            <div
              key={handle}
              className="resize-handle"
              onMouseDown={(e) => handleResizeStart(e, handle)}
              style={{
                position: 'absolute',
                background: '#007bff',
                border: '2px solid white',
                borderRadius: handle.includes('-') ? '50%' : '2px',
                width: handle.includes('-') ? '14px' : '10px',
                height: handle.includes('-') ? '14px' : '10px',
                zIndex: 10,
                cursor: getCursorForHandle(handle),
                boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                ...(handle === 'top-left' && { top: '-7px', left: '-7px' }),
                ...(handle === 'top' && { top: '-5px', left: '50%', transform: 'translateX(-50%)' }),
                ...(handle === 'top-right' && { top: '-7px', right: '-7px' }),
                ...(handle === 'right' && { top: '50%', right: '-5px', transform: 'translateY(-50%)' }),
                ...(handle === 'bottom-right' && { bottom: '-7px', right: '-7px' }),
                ...(handle === 'bottom' && { bottom: '-5px', left: '50%', transform: 'translateX(-50%)' }),
                ...(handle === 'bottom-left' && { bottom: '-7px', left: '-7px' }),
                ...(handle === 'left' && { top: '50%', left: '-5px', transform: 'translateY(-50%)' })
              }}
            />
          ))}
        </div>
        <small style={{ color: '#666', fontSize: '0.8rem', marginTop: '0.5rem', display: 'block' }}>
          💡 Cliquez et glissez pour repositionner • Tirez les poignées bleues pour redimensionner
        </small>
      </div>

      {/* Zoom Slider */}
      <div style={{ marginBottom: '1rem' }}>
        <label style={{ 
          display: 'block', 
          marginBottom: '0.5rem', 
          fontWeight: 'bold',
          fontSize: '0.9rem'
        }}>
          🔍 Zoom: {Math.round(scale * 100)}%
        </label>
        <input
          type="range"
          min="0.5"
          max="3"
          step="0.1"
          value={scale}
          onChange={(e) => handleScaleChange(parseFloat(e.target.value))}
          style={{
            width: '100%',
            height: '8px',
            borderRadius: '4px',
            background: `linear-gradient(to right, #007bff 0%, #007bff ${((scale - 0.5) / 2.5) * 100}%, #ddd ${((scale - 0.5) / 2.5) * 100}%, #ddd 100%)`,
            outline: 'none',
            cursor: 'pointer'
          }}
        />
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          fontSize: '0.75rem',
          color: '#666',
          marginTop: '0.25rem'
        }}>
          <span>50%</span>
          <span>100%</span>
          <span>300%</span>
        </div>
      </div>

      {/* Position Info */}
      <div style={{ 
        padding: '0.75rem',
        background: '#e7f3ff',
        borderRadius: '0.5rem',
        marginBottom: '1rem',
        fontSize: '0.85rem'
      }}>
        <strong>Position actuelle:</strong>
        <div style={{ marginTop: '0.25rem', color: '#666' }}>
          X: {Math.round(position.x)}px | Y: {Math.round(position.y)}px | Zoom: {Math.round(scale * 100)}%
        </div>
      </div>

      {/* Center Controls */}
      <div style={{ marginBottom: '1rem' }}>
        <label style={{ 
          display: 'block', 
          marginBottom: '0.5rem', 
          fontWeight: 'bold',
          fontSize: '0.9rem'
        }}>
          🎯 Centrage rapide
        </label>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            onClick={handleCenterHorizontally}
            style={{
              flex: 1,
              padding: '0.6rem',
              border: '1px solid #007bff',
              borderRadius: '0.5rem',
              background: 'white',
              color: '#007bff',
              cursor: 'pointer',
              fontWeight: '500',
              fontSize: '0.85rem',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.4rem'
            }}
            onMouseOver={(e) => e.target.style.background = '#e7f3ff'}
            onMouseOut={(e) => e.target.style.background = 'white'}
          >
            <i className="fas fa-arrows-alt-h"></i> Centrer ↔
          </button>
          <button
            onClick={handleCenterVertically}
            style={{
              flex: 1,
              padding: '0.6rem',
              border: '1px solid #007bff',
              borderRadius: '0.5rem',
              background: 'white',
              color: '#007bff',
              cursor: 'pointer',
              fontWeight: '500',
              fontSize: '0.85rem',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.4rem'
            }}
            onMouseOver={(e) => e.target.style.background = '#e7f3ff'}
            onMouseOut={(e) => e.target.style.background = 'white'}
          >
            <i className="fas fa-arrows-alt-v"></i> Centrer ↕
          </button>
          <button
            onClick={handleCenterBoth}
            style={{
              flex: 1,
              padding: '0.6rem',
              border: '1px solid #28a745',
              borderRadius: '0.5rem',
              background: 'white',
              color: '#28a745',
              cursor: 'pointer',
              fontWeight: '500',
              fontSize: '0.85rem',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.4rem'
            }}
            onMouseOver={(e) => e.target.style.background = '#d4edda'}
            onMouseOut={(e) => e.target.style.background = 'white'}
          >
            <i className="fas fa-crosshairs"></i> Tout centrer
          </button>
        </div>
      </div>

      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: '0.75rem' }}>
        <button
          onClick={handleReset}
          style={{
            flex: 1,
            padding: '0.75rem',
            border: '1px solid #dc3545',
            borderRadius: '0.5rem',
            background: 'white',
            color: '#dc3545',
            cursor: 'pointer',
            fontWeight: 'bold',
            fontSize: '0.9rem',
            transition: 'all 0.2s'
          }}
          onMouseOver={(e) => e.target.style.background = '#fff5f5'}
          onMouseOut={(e) => e.target.style.background = 'white'}
        >
          <i className="fas fa-undo"></i> Réinitialiser
        </button>
        <button
          onClick={handleSave}
          style={{
            flex: 2,
            padding: '0.75rem',
            border: 'none',
            borderRadius: '0.5rem',
            background: '#28a745',
            color: 'white',
            cursor: 'pointer',
            fontWeight: 'bold',
            fontSize: '0.9rem',
            transition: 'all 0.2s',
            boxShadow: '0 2px 8px rgba(40,167,69,0.3)'
          }}
          onMouseOver={(e) => e.target.style.background = '#218838'}
          onMouseOut={(e) => e.target.style.background = '#28a745'}
        >
          <i className="fas fa-check"></i> Enregistrer la position
        </button>
      </div>
    </div>
  );
};

export default ImagePositionEditor;
